/**
 * PAX Android Bridge
 * -------------------
 * When FlavorFeed POS runs INSIDE the PAX wrapper APK (on A920Pro / A77
 * etc.), the WebView injects a JavaScript interface called `FlavorFeedPAX`
 * that exposes the device's native capabilities:
 *
 *   • pay({ amount_cents, tip_cents, order_id }) -> JSON string
 *   • printReceipt(html) -> "ok" | "error: ..."
 *   • openCashDrawer() -> "ok" | "error: ..."
 *   • getDeviceInfo() -> { serial, model, firmware }
 *   • scanBarcode() -> "<barcode>" | ""
 *
 * Every method returns a JSON-encoded string (Android WebView limitation —
 * @JavascriptInterface cannot return raw objects). Callers must JSON.parse.
 *
 * In a normal browser (Chrome/Safari/iPad Safari) this object is `undefined`
 * and isPaxDevice() returns false; everything falls back to the existing
 * Valor / Clover / Fiserv cloud-API processors.
 *
 * BROWSER DEV MODE: when localStorage["pax_dev_mode_enabled"] === "true",
 * a JS-only simulator is auto-installed at import time so cashier flows
 * see window.FlavorFeedPAX exactly as on real hardware. Toggle it from
 * POS Settings → Payments → PAX Native.
 */

import { logPaxTransaction } from './paxTxnLog';

const DEV_MODE_KEY = 'pax_dev_mode_enabled';

function installBrowserSimulator() {
  if (typeof window === 'undefined') return;
  if (window.FlavorFeedPAX && window.FlavorFeedPAX.__simulator !== true) return;
  window.FlavorFeedPAX = {
    __simulator: true,
    pay: (paramsJson) => {
      let p = {};
      try { p = JSON.parse(paramsJson || '{}'); } catch { p = {}; }
      return JSON.stringify({
        success: true,
        authCode: 'SIMOK',
        transactionNo: String(Date.now()),
        maskedPan: '****4242',
        issuer: 'VISA',
        responseText: 'APPROVED (BROWSER SIMULATOR)',
        amount_cents: p.amount_cents,
        tip_cents: p.tip_cents,
        order_id: p.order_id,
      });
    },
    printReceipt: () => 'ok',
    openCashDrawer: () => 'ok',
    scanBarcode: () => '',
    getDeviceInfo: () => JSON.stringify({
      serial: 'PAX-SIM-BROWSER',
      model: 'A920Pro (Simulator)',
      firmware: 'sim-1.0.0',
      manufacturer: 'PAX',
    }),
  };
}

// Auto-install on import if the merchant flipped the Dev Mode toggle.
if (typeof window !== 'undefined') {
  try {
    if (localStorage.getItem(DEV_MODE_KEY) === 'true') installBrowserSimulator();
  } catch { /* ignore SSR / private mode */ }
}

// Read the bridge dynamically every call — the simulator can be toggled
// on/off at runtime from POS Settings → Payments → PAX Native, and the
// real Android WebView may inject the interface AFTER initial page load.
function getBridge() {
  return typeof window !== 'undefined' ? window.FlavorFeedPAX : undefined;
}

export function isPaxDevice() {
  const b = getBridge();
  return !!(b && typeof b.pay === 'function');
}

export function getPaxDeviceInfo() {
  const b = getBridge();
  if (!b || typeof b.getDeviceInfo !== 'function') return null;
  try {
    return JSON.parse(b.getDeviceInfo());
  } catch (e) {
    console.warn('PAX getDeviceInfo parse failed:', e);
    return null;
  }
}

/**
 * Run a card sale on the PAX terminal hardware.
 *
 * @param {object} params
 * @param {number} params.amount_cents  total to charge, in cents
 * @param {number} [params.tip_cents=0]
 * @param {string} [params.order_id]
 * @returns {Promise<{success:boolean, authCode?:string, transactionNo?:string,
 *                    maskedPan?:string, issuer?:string, responseText?:string,
 *                    error?:string}>}
 */
export async function paxPay({ amount_cents, tip_cents = 0, order_id = '', zip_code = '', disable_avs = false }) {
  const b = getBridge();
  if (!b) {
    const r = { success: false, error: 'PAX bridge not available' };
    logPaxTransaction('SALE', { amount_cents, tip_cents, order_id }, r);
    return r;
  }
  try {
    // Auto-inject the merchant default ZIP from POS Settings if the caller
    // didn't supply one — used to satisfy A35 AVS checks (error 100003).
    let zip = zip_code;
    if (!zip) {
      try { zip = localStorage.getItem('merchant_zip') || ''; } catch (e) { /* noop */ }
    }
    const params = { amount_cents, tip_cents, order_id, zip_code: zip, disable_avs };

    // Prefer the non-blocking native path so the WebView's JS thread
    // stays alive — the cashier's Cancel button must remain clickable
    // while the terminal is prompting for a card. Falls back to the
    // legacy synchronous bridge for older APKs that don't expose
    // `payAsync` yet.
    if (typeof b.payAsync === 'function') {
      const parsed = await new Promise((resolve) => {
        const resolverName = `__paxResolve_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        // 130s safety net — slightly longer than the Kotlin TIMEOUT_MS
        // (120s) so the bridge ALWAYS has a chance to fire its own
        // timeout error first.
        const watchdog = setTimeout(() => {
          try { delete window[resolverName]; } catch { /* noop */ }
          resolve({ success: false, error: 'Terminal call timed out (130s) — no response from PAX bridge.' });
        }, 130_000);
        window[resolverName] = (rawJson) => {
          clearTimeout(watchdog);
          try { delete window[resolverName]; } catch { /* noop */ }
          try { resolve(JSON.parse(rawJson || '{}')); }
          catch (e) { resolve({ success: false, error: 'PAX response parse failed: ' + e.message }); }
        };
        try {
          b.payAsync(JSON.stringify(params), resolverName);
        } catch (e) {
          clearTimeout(watchdog);
          try { delete window[resolverName]; } catch { /* noop */ }
          resolve({ success: false, error: e.message || 'payAsync dispatch failed' });
        }
      });
      logPaxTransaction('SALE', { amount_cents, tip_cents, order_id }, parsed);
      return parsed;
    }

    // Legacy synchronous path (APK ≤ v1.0.15)
    if (typeof b.pay !== 'function') {
      const r = { success: false, error: 'PAX bridge.pay not available' };
      logPaxTransaction('SALE', params, r);
      return r;
    }
    const raw = b.pay(JSON.stringify(params));
    const parsed = JSON.parse(raw || '{}');
    logPaxTransaction('SALE', params, parsed);
    return parsed;
  } catch (e) {
    const r = { success: false, error: e.message || 'PAX call failed' };
    logPaxTransaction('SALE', { amount_cents, tip_cents, order_id }, r);
    return r;
  }
}

/**
 * Cancel an in-flight PAX transaction. Closes the bridge's open socket,
 * which the BroadPOS terminal interprets as "host disconnected" and
 * returns its card-prompt back to idle within ~1s.
 *
 * Safe to call when nothing is in flight — bridge will report
 * `{success:true, closed:false}`.
 */
export function paxCancel() {
  const b = getBridge();
  if (!b || typeof b.cancel !== 'function') {
    return { success: false, error: 'PAX bridge.cancel not available — APK upgrade required' };
  }
  try {
    const raw = b.cancel();
    return JSON.parse(raw || '{}');
  } catch (e) {
    return { success: false, error: e.message || 'cancel failed' };
  }
}

export function paxPrintReceipt(html) {
  const b = getBridge();
  if (!b || typeof b.printReceipt !== 'function') return false;
  try {
    const r = b.printReceipt(html);
    return r === 'ok' || r === '"ok"';
  } catch (e) {
    console.warn('PAX printReceipt failed:', e);
    return false;
  }
}

export function paxOpenCashDrawer() {
  const b = getBridge();
  if (!b || typeof b.openCashDrawer !== 'function') return false;
  try {
    const r = b.openCashDrawer();
    return r === 'ok' || r === '"ok"';
  } catch (e) {
    console.warn('PAX openCashDrawer failed:', e);
    return false;
  }
}

export function paxScanBarcode() {
  const b = getBridge();
  if (!b || typeof b.scanBarcode !== 'function') {
    return Promise.resolve('');
  }
  return new Promise((resolve) => {
    try {
      const r = b.scanBarcode();
      resolve(typeof r === 'string' ? r : '');
    } catch {
      resolve('');
    }
  });
}

// ───────────────────────────────────────────────────────────────────────
// Empire Paytech POSLink TCP wrappers (added Feb 2026).
// These pair with the native methods exposed by PaxBridge.kt:
//   setTerminalConfig / getTerminalConfig / ping / voidTxn / refund /
//   adjustTip / preauth / capture / batchClose
// ───────────────────────────────────────────────────────────────────────

function _call(method, paramsObj) {
  const b = getBridge();
  const label = method.replace(/Txn$/, '').toUpperCase();
  if (!b || typeof b[method] !== 'function') {
    const r = { success: false, error: `PAX bridge method "${method}" not available` };
    logPaxTransaction(label, paramsObj || {}, r);
    return r;
  }
  try {
    const raw = paramsObj === undefined ? b[method]() : b[method](JSON.stringify(paramsObj));
    const parsed = JSON.parse(raw || '{}');
    logPaxTransaction(label, paramsObj || {}, parsed);
    return parsed;
  } catch (e) {
    const r = { success: false, error: e.message || `${method} failed` };
    logPaxTransaction(label, paramsObj || {}, r);
    return r;
  }
}

export function paxSetTerminalConfig({ host, port = 10009 }) {
  return _call('setTerminalConfig', { host, port });
}

export function paxGetTerminalConfig() {
  const b = getBridge();
  if (!b || typeof b.getTerminalConfig !== 'function') return null;
  try { return JSON.parse(b.getTerminalConfig() || '{}'); } catch { return null; }
}

export function paxPing()         { return _call('ping'); }
export function paxVoid(p)        { return _call('voidTxn', p); }
export function paxRefund(p)      { return _call('refund', p); }
export function paxAdjustTip(p)   { return _call('adjustTip', p); }
export function paxPreauth(p)     { return _call('preauth', p); }
export function paxCapture(p)     { return _call('capture', p); }
export function paxBatchClose()   { return _call('batchClose'); }

/**
 * Returns the APK build metadata + the list of native bridge methods
 * implemented in this APK's PaxBridge.kt. Use this to render an
 * "About → Native App" panel and to detect when a future feature
 * requires a fresh native build (the new capability won't be in the
 * `capabilities` array on the old APK).
 *
 * Falls back to a non-PAX descriptor when running outside the APK so the
 * UI can render "Web only" gracefully.
 */
export function paxGetVersionInfo() {
  const b = getBridge();
  if (!b || typeof b.getVersionInfo !== 'function') {
    return {
      packageName: null,
      versionName: 'web',
      versionCode: 0,
      poslinkVersion: null,
      androidSdk: null,
      buildModel: 'Browser (no native APK)',
      capabilities: [],
      isNative: false,
    };
  }
  try {
    const parsed = JSON.parse(b.getVersionInfo() || '{}');
    return { ...parsed, isNative: true };
  } catch (e) {
    return { error: e.message, isNative: false, capabilities: [] };
  }
}

const paxBridge = {
  isPaxDevice,
  getPaxDeviceInfo,
  paxPay,
  paxPrintReceipt,
  paxOpenCashDrawer,
  paxScanBarcode,
};

export default paxBridge;
